import { Loader2 } from "lucide-react";
import {
  useDataProvider,
  useNotify,
  useRefresh,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { aFechaISO } from "../dominio/fechas";
import { importeATexto as aTexto, leerImporte } from "../dominio/importes";
import type { ConceptoCobro, Negocio } from "../dominio/tipos";

/**
 * Registrar un cobro (Robert, 02/10/2026: Cobrado = primer 50 %). El primero pasa el
 * proyecto a Clientes · Arranque y DPA; los siguientes solo se apuntan. Guarda la base
 * imponible SIN IVA, la fecha, el importe total aceptado y si es de proyecto o de
 * mantenimiento: la comisión del setter (5 %) la calcula la base de datos y el
 * mantenimiento nunca lleva comisión (Jan y Robert, 06-07/10/2026).
 */
export const CobroDialog = ({
  proyecto,
  primero,
  open,
  onOpenChange,
  onRegistrado,
}: {
  proyecto: Pick<
    Negocio,
    "proyecto_id" | "nombre" | "valor_estimado" | "importe_total"
  > &
    Partial<Pick<Negocio, "etapa">> & { proyecto_id: Identifier };
  /** true si es el primer cobro (Ventas → Clientes). */
  primero: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistrado?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [total, setTotal] = useState("");
  const [importe, setImporte] = useState("");
  const [fecha, setFecha] = useState("");
  const [nota, setNota] = useState("");
  const [concepto, setConcepto] = useState<ConceptoCobro>("proyecto");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = proyecto.importe_total ?? proyecto.valor_estimado ?? null;
    setTotal(aTexto(base));
    setImporte(primero && base ? aTexto(base / 2) : "");
    setFecha(aFechaISO(new Date()));
    setNota("");
    setConcepto(
      proyecto.etapa === "mantenimiento" ? "mantenimiento" : "proyecto",
    );
    setGuardando(false);
  }, [
    open,
    primero,
    proyecto.importe_total,
    proyecto.valor_estimado,
    proyecto.etapa,
  ]);

  const guardar = async () => {
    const cobrado = leerImporte(importe);
    const importeTotal = total.trim() ? leerImporte(total) : null;
    if (cobrado == null || cobrado <= 0) {
      notify("nevora.cobro.errores.importe", { type: "warning" });
      return;
    }
    if (importeTotal != null && importeTotal < 0) {
      notify("nevora.cobro.errores.importe_total", { type: "warning" });
      return;
    }
    if (!fecha) {
      notify("nevora.cobro.errores.fecha", { type: "warning" });
      return;
    }
    setGuardando(true);
    try {
      await dataProvider.registrarCobro({
        proyectoId: proyecto.proyecto_id,
        importe: cobrado,
        fecha,
        importeTotal,
        nota,
        concepto,
      });
      notify(
        primero ? "nevora.cobro.guardado_clientes" : "nevora.cobro.guardado",
        { type: "success" },
      );
      refresh();
      onRegistrado?.();
      onOpenChange(false);
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="titulo-seccion">
            {translate(
              primero
                ? "nevora.cobro.titulo_primero"
                : "nevora.cobro.titulo_otro",
            )}
          </DialogTitle>
          <DialogDescription>
            {proyecto.nombre} ·{" "}
            {translate(
              primero
                ? "nevora.cobro.ayuda_primero"
                : "nevora.cobro.ayuda_otro",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cobro_total">
              {translate("nevora.cobro.importe_total")}
            </Label>
            <Input
              id="cobro_total"
              inputMode="decimal"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cobro_importe">
              {translate("nevora.cobro.importe")}
            </Label>
            <Input
              id="cobro_importe"
              inputMode="decimal"
              value={importe}
              onChange={(e) => setImporte(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cobro_fecha">
              {translate("nevora.cobro.fecha")}
            </Label>
            <Input
              id="cobro_fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              required
            />
          </div>
          {!primero ? (
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium mb-1">
                {translate("nevora.cobro.concepto")}
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {(["proyecto", "mantenimiento"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={concepto === c}
                    onClick={() => setConcepto(c)}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm transition-colors",
                      concepto === c
                        ? "border-foreground bg-accent font-medium"
                        : "border-border bg-card hover:bg-muted",
                    )}
                  >
                    {translate(`nevora.cobro.concepto_${c}`)}
                  </button>
                ))}
              </div>
            </fieldset>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="cobro_nota">{translate("nevora.cobro.nota")}</Label>
            <Input
              id="cobro_nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {translate("nevora.cobro.comision_ayuda")}
          </p>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {translate("ra.action.cancel")}
          </Button>
          <Button type="button" onClick={guardar} disabled={guardando}>
            {guardando ? (
              <Loader2 className="size-4 animate-spin mr-2" />
            ) : null}
            {translate("nevora.cobro.guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
